import { useEffect, useRef, useState } from 'react';
import { formatPhoneDisplay, formatShortDate } from '../../utils/whatsapp';
import ContactAvatar from './ContactAvatar';
import { initialsFor } from './initials';

export { initialsFor };

/** "10:05 AM" for something said today, a short date otherwise (same split as ChatThread's day separators). */
const listTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  return sameDay ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : formatShortDate(iso);
};

/**
 * The left-hand conversation list: avatar initials, name or phone, last message, time, an unread
 * badge and an "Opted out" chip. `selectedPhone` drives `aria-current` on the open conversation.
 * @param {{conversations: any[], loaded: boolean, loading: boolean, error: string, onRetry: () => void, selectedPhone: string | null, onSelect: (phone: string) => void, hasMore: boolean, loadingMore: boolean, onLoadMore: () => void}} props
 */
const ConversationList = ({
  conversations,
  loaded,
  loading,
  error,
  onRetry,
  selectedPhone,
  onSelect,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  // Phones whose unread count just went up get a short highlight. Never on the first load or when
  // "Load more" appends older rows; timers live in a ref so a re-render can't strand the highlight.
  const prevUnread = useRef(/** @type {Map<string, number> | null} */ (null));
  const pagedRef = useRef(false);
  const timers = useRef(/** @type {any[]} */ ([]));
  const [flashing, setFlashing] = useState(/** @type {string[]} */ ([]));
  if (loadingMore) pagedRef.current = true;
  useEffect(() => {
    if (!loaded) return;
    const next = new Map(conversations.map((c) => [c.phone, c.unread_count || 0]));
    const before = prevUnread.current;
    prevUnread.current = next;
    if (!before) return;
    if (pagedRef.current) {
      pagedRef.current = false;
      return;
    }
    const bumped = conversations.filter((c) => (c.unread_count || 0) > (before.get(c.phone) ?? 0)).map((c) => c.phone);
    if (bumped.length === 0) return;
    setFlashing((cur) => [...new Set([...cur, ...bumped])]);
    timers.current.push(setTimeout(() => setFlashing((cur) => cur.filter((p) => !bumped.includes(p))), 3000));
  }, [conversations, loaded]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  if (!loaded && loading) {
    return <p className="p-6 text-center text-sm text-muted-foreground">Loading chats...</p>;
  }

  if (!loaded && error) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-foreground/80">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    );
  }

  if (loaded && conversations.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="font-medium text-foreground">No conversations yet</p>
        <p className="mt-1 text-sm text-muted-foreground">Replies from customers will appear here.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <ul aria-label="Conversations" className="divide-y divide-border/60">
        {conversations.map((c) => {
          const selected = c.phone === selectedPhone;
          return (
            <li key={c.phone}>
              <button
                type="button"
                aria-current={selected}
                onClick={() => onSelect(c.phone)}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 ${
                  selected ? 'bg-muted' : flashing.includes(c.phone) ? 'bg-green-50 dark:bg-green-950/50' : ''
                } transition-colors duration-1000`}
              >
                <ContactAvatar name={c.name} phone={c.phone} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span
                      className={`truncate text-sm text-foreground ${c.unread_count > 0 ? 'font-bold' : 'font-medium'}`}
                    >
                      {c.name || formatPhoneDisplay(c.phone)}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground/70">{listTime(c.last_message_at)}</span>
                  </span>
                  <span className="mt-0.5 flex items-center justify-between gap-2">
                    <span
                      className={`truncate text-sm ${c.unread_count > 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                    >
                      {c.last_message_text || ''}
                    </span>
                    {c.unread_count > 0 && (
                      <span
                        aria-label={`${c.unread_count} unread`}
                        className="shrink-0 rounded-full bg-[#25D366] px-[7px] py-[1px] text-xs font-medium text-white"
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </span>
                  {c.suppressed && (
                    <span className="mt-1 inline-block rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      Opted out
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="flex justify-center py-3">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="text-sm text-[#008069] hover:underline disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationList;
